import { AlertTriangle, ArrowLeft, RefreshCw, WifiOff } from "lucide-react";
import { useEffect, useState } from "react";
import {
	isRouteErrorResponse,
	useNavigate,
	useRouteError,
} from "react-router-dom";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";

type ErrorKind = "offline" | "chunk" | "not-found" | "generic";

function getErrorMessage(error: unknown): string {
	if (isRouteErrorResponse(error)) {
		const data = error.data;
		if (typeof data === "string" && data.trim()) return data;
		if (
			data &&
			typeof data === "object" &&
			"message" in data &&
			typeof (data as { message: unknown }).message === "string"
		) {
			return (data as { message: string }).message;
		}
		return error.statusText || `Error ${error.status}`;
	}
	if (error instanceof Error) return error.message;
	if (typeof error === "string") return error;
	return "Something went wrong while loading this page.";
}

function classifyError(error: unknown, isOffline: boolean): ErrorKind {
	if (isOffline) return "offline";

	const message = getErrorMessage(error).toLowerCase();
	if (
		message.includes("failed to fetch dynamically imported module") ||
		message.includes("error loading dynamically imported module") ||
		message.includes("loading chunk") ||
		message.includes("importing a module script failed")
	) {
		return "chunk";
	}

	if (isRouteErrorResponse(error) && error.status === 404) {
		return "not-found";
	}

	return "generic";
}

function getCopy(kind: ErrorKind): {
	title: string;
	description: string;
	icon: "offline" | "alert";
} {
	switch (kind) {
		case "offline":
			return {
				title: "You’re offline",
				description:
					"This page couldn’t load because there’s no network connection. Stay on this screen until you’re back online, then try again.",
				icon: "offline",
			};
		case "chunk":
			return {
				title: "Page couldn’t be loaded",
				description:
					"We couldn’t download this screen. This often happens when the connection drops or a new version of the app was deployed. Retry once you’re back online.",
				icon: "offline",
			};
		case "not-found":
			return {
				title: "Page not found",
				description:
					"The page you’re looking for doesn’t exist or may have moved.",
				icon: "alert",
			};
		default:
			return {
				title: "Something went wrong",
				description:
					"An unexpected error occurred while opening this page. You can retry or go back to the dashboard.",
				icon: "alert",
			};
	}
}

export interface RouteErrorPageProps {
	/** When true, fills the viewport (login / top-level). Embedded keeps layout chrome via Outlet. */
	fullPage?: boolean;
}

export function RouteErrorPage({ fullPage = false }: RouteErrorPageProps) {
	const error = useRouteError();
	const navigate = useNavigate();
	const isOnline = useOnlineStatus();
	const isOffline = !isOnline;
	const [hadOfflineSession, setHadOfflineSession] = useState(isOffline);
	const [retryHint, setRetryHint] = useState<string | null>(null);

	useEffect(() => {
		if (isOffline) {
			setHadOfflineSession(true);
			setRetryHint(null);
			return;
		}
		if (hadOfflineSession) {
			setRetryHint("You’re back online — tap Try again to reload this page.");
		}
	}, [isOffline, hadOfflineSession]);

	const kind = classifyError(error, isOffline);
	const copy = getCopy(kind);
	const detail =
		import.meta.env.DEV && kind === "generic" ? getErrorMessage(error) : null;

	const handleRetry = () => {
		// Reloading while offline triggers the browser’s native offline page (e.g. Chrome dino).
		if (!navigator.onLine) {
			setRetryHint(
				"Still offline — reconnect to the internet, then try again.",
			);
			return;
		}
		window.location.reload();
	};

	const handleHome = () => {
		navigate("/", { replace: true });
	};

	const Icon = copy.icon === "offline" ? WifiOff : AlertTriangle;

	return (
		<div
			className={
				fullPage
					? "flex min-h-screen items-center justify-center bg-bg-page p-6"
					: "flex min-h-[320px] items-center justify-center p-6"
			}
		>
			<div className="w-full max-w-md overflow-hidden rounded-2xl border border-border bg-bg-card shadow-sm">
				<div className="border-b border-border-light px-6 py-5">
					<div className="flex items-start gap-3">
						<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--color-role-homeowner-bg)] text-accent">
							<Icon className="h-5 w-5" aria-hidden />
						</div>
						<div className="min-w-0">
							<h1 className="text-lg font-semibold text-primary">
								{copy.title}
							</h1>
							<p className="mt-1 text-sm leading-6 text-primary-muted">
								{copy.description}
							</p>
							{isOffline ? (
								<p className="mt-2 text-xs font-medium text-status-invite-expired">
									Network status: offline
								</p>
							) : null}
							{retryHint ? (
								<p
									className={`mt-2 text-xs font-medium ${
										isOffline
											? "text-status-invite-expired"
											: "text-status-active"
									}`}
								>
									{retryHint}
								</p>
							) : null}
						</div>
					</div>
				</div>

				{detail ? (
					<div className="border-b border-border-light bg-bg-input px-6 py-3">
						<p className="break-words font-mono text-xs text-body">{detail}</p>
					</div>
				) : null}

				<div className="flex flex-wrap items-center justify-end gap-2 px-6 py-4">
					<button
						type="button"
						onClick={handleHome}
						className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-input-text hover:bg-bg-input"
					>
						<ArrowLeft className="h-4 w-4" aria-hidden />
						Go to home
					</button>
					<button
						type="button"
						onClick={handleRetry}
						disabled={isOffline}
						title={
							isOffline
								? "Reconnect to the internet to retry"
								: "Reload this page"
						}
						className="inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
					>
						<RefreshCw className="h-4 w-4" aria-hidden />
						Try again
					</button>
				</div>
			</div>
		</div>
	);
}
